import React, { useState } from 'react';
import Input from '../ui/Input';

const CreateRelicModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    power: 1,
    effects: {
      strength: 0,
      defense: 0,
      speed: 0,
      magic: 0
    },
    image: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar erro do campo quando usuário digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEffectChange = (effectName, value) => {
    const numericValue = Math.max(0, parseInt(value) || 0);
    setFormData(prev => ({
      ...prev,
      effects: {
        ...prev.effects,
        [effectName]: numericValue
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    const power = parseInt(formData.power);
    if (!power || power < 1 || power > 100) {
      newErrors.power = 'Poder deve estar entre 1 e 100';
    }

    // Pelo menos um efeito deve ser maior que 0 (opcional)
    // const hasEffects = Object.values(formData.effects).some(value => value > 0);
    // if (!hasEffects) {
    //   newErrors.effects = 'Pelo menos um efeito deve ser maior que 0';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await onSubmit({
        ...formData,
        power: parseInt(formData.power)
      });
      // Reset form
      setFormData({
        name: '',
        description: '',
        power: 1,
        effects: {
          strength: 0,
          defense: 0,
          speed: 0,
          magic: 0
        },
        image: ''
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Erro ao criar relíquia:', error);
      setErrors({ submit: 'Erro ao criar relíquia. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      power: 1,
      effects: {
        strength: 0,
        defense: 0,
        speed: 0,
        magic: 0
      },
      image: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const getPowerColor = (power) => {
    if (power >= 90) return 'text-purple-600';
    if (power >= 70) return 'text-orange-600';
    if (power >= 50) return 'text-blue-600';
    if (power >= 30) return 'text-green-600';
    return 'text-gray-600';
  };

  const effectLabels = {
    strength: 'Força',
    defense: 'Defesa',
    speed: 'Velocidade',
    magic: 'Magia'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 px-6 pb-6 pt-6 text-left shadow-xl border border-white/10 transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                🏺 Criar Nova Relíquia
              </h3>
              <p className="text-sm text-gray-400">
                Adicione uma nova relíquia ao sistema
              </p>
            </div>

            {errors.submit && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm backdrop-blur-sm">
                {errors.submit}
              </div>
            )}

            <div className="space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nome da Relíquia *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ex: Amuleto dos Ventos"
                  className={`w-full ${errors.name ? 'border-red-300' : ''}`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                )}
              </div>

              {/* Poder */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Poder (1-100) *
                </label>
                <Input
                  type="number"
                  name="power"
                  min="1"
                  max="100"
                  value={formData.power}
                  onChange={handleChange}
                  className={`w-full ${errors.power ? 'border-red-300' : ''}`}
                />
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 bg-slate-700/50 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        formData.power >= 90 ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                        formData.power >= 70 ? 'bg-gradient-to-r from-orange-500 to-yellow-500' :
                        formData.power >= 50 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                        'bg-gradient-to-r from-green-500 to-emerald-500'
                      }`}
                      style={{ width: `${formData.power}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold ${getPowerColor(parseInt(formData.power))}`}>
                    {formData.power}/100
                  </span>
                </div>
                {errors.power && (
                  <p className="mt-1 text-sm text-red-400">{errors.power}</p>
                )}
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Descrição *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Descreva a relíquia, sua origem e poderes..."
                  className={`w-full px-3 py-2.5 bg-slate-700/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all ${
                    errors.description ? 'border-red-500/50' : 'border-white/20'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-400">{errors.description}</p>
                )}
              </div>
            </div>

            {/* Botões */}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-lg text-gray-300 hover:text-white font-medium transition-all duration-300 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20 disabled:opacity-50"
              >
                {loading ? 'Criando...' : 'Criar Relíquia'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateRelicModal;